import { Autocomplete, Box, Paper, TextField } from "@mui/material";
import { SearchTextField } from "@/controls/SearchTextField";
import type { BreakLine, ShiftHours } from "@/shared/models/BreakRule";

export type BreakScheduleFiltersState = {
	quickFilterText: string;
	assetId: number | null;
	shiftHours: ShiftHours | null;
};

export const BREAK_SCHEDULE_FILTERS_INITIAL: BreakScheduleFiltersState = {
	quickFilterText: "",
	assetId: null,
	shiftHours: null,
};

type LineOption =
	| Pick<BreakLine, "AssetId" | "AssetName">
	| { AssetId: null; AssetName: "All" };
const ALL_LINES: LineOption = { AssetId: null, AssetName: "All" };

type ShiftOption = ShiftHours | "all";
const SHIFT_OPTIONS: ShiftOption[] = ["all", 8, 12];

type Props = {
	filters: BreakScheduleFiltersState;
	lines: BreakLine[];
	onChange: (filters: BreakScheduleFiltersState) => void;
};

export const BreakScheduleFilters = ({ filters, lines, onChange }: Props) => {
	const lineOptions: LineOption[] = [ALL_LINES, ...lines];
	return (
		<Paper
			elevation={1}
			sx={{
				p: 2,
				display: "inline-flex",
				flexWrap: "wrap",
				maxWidth: "100%",
				boxSizing: "border-box",
				alignItems: "center",
				gap: 1.5,
			}}
		>
			<Box sx={{ minWidth: 220 }}>
				<SearchTextField
					value={filters.quickFilterText}
					onChange={(text) => onChange({ ...filters, quickFilterText: text })}
					label="Search"
				/>
			</Box>
			<Box sx={{ minWidth: 240 }}>
				<Autocomplete
					size="small"
					disableClearable
					options={lineOptions}
					getOptionLabel={(line) => line.AssetName}
					isOptionEqualToValue={(a, b) => a.AssetId === b.AssetId}
					value={
						lineOptions.find((l) => l.AssetId === filters.assetId) ?? ALL_LINES
					}
					onChange={(_, line) =>
						onChange({ ...filters, assetId: line.AssetId })
					}
					renderInput={(params) => <TextField {...params} label="Line" />}
				/>
			</Box>
			<Box sx={{ minWidth: 160 }}>
				<Autocomplete
					size="small"
					disableClearable
					options={SHIFT_OPTIONS}
					getOptionLabel={(h) => (h === "all" ? "All" : `${h}h shifts`)}
					value={filters.shiftHours ?? "all"}
					onChange={(_, h) =>
						onChange({ ...filters, shiftHours: h === "all" ? null : h })
					}
					renderInput={(params) => <TextField {...params} label="Shift" />}
				/>
			</Box>
		</Paper>
	);
};
